import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import { RulerControl } from "mapbox-gl-controls";
import styled, { ThemeProvider } from "styled-components/macro";
import createTheme from "../../theme";
import debounce from "lodash.debounce";
import { create } from "jss";
import {
  jssPreset,
  StylesProvider,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/core/styles";
import {
  countiesFill,
  countiesLine,
  handleCopyCoords,
  locationsLabelsLayer,
  locationsLayer,
  managementAreaFill,
  managementAreaLine,
} from "../../utils/map";
import CoordinatesPopup from "./components/CoordinatesPopup";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { coordinatesGeocoder } from "../../pages/publicMap/hooks/useMap/mapUtils";
import LocationsControl from "./LocationsControl";
import ReactDOM from "react-dom";
import Popup from "../../pages/publicMap/popup";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import axios from "axios";
import {
  BASEMAP_STYLES,
  DEFAULT_MAP_CENTER,
} from "../../pages/publicMap/constants";
import { useApp } from "../../AppProvider";
import ZonesControl from "./ZonesControl";
import CountiesControl from "./CountiesControl";
import MarkerControl from "./MarkerControl";

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById("jss-insertion-point"),
});

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const Container = styled.div`
  height: 320px;
  width: 100%;
`;

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Instructions = styled.div`
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  position: absolute;
  text-align: center;
  left: 50%;
  margin-right: -50%;
  transform: translate(-50%, 0);
  padding: 5px 10px;
  font-size: 11px;
  line-height: 18px;
  border-radius: 3px;
  z-index: 1000;
  display: block;
`;

const RegLatLongMap = ({ filterValues, setFilterValues }) => {
  const { currentUser } = useApp();
  const [map, setMap] = useState();
  const [mapIsLoaded, setMapIsLoaded] = useState(false);
  const [locationsVisible, setLocationsVisible] = useState(false);
  const [zonesVisible, setZonesVisible] = useState(true);
  const [countiesVisible, setCountiesVisible] = useState(false);
  const [markerEnabled, setMarkerEnabled] = useState(true);
  const markerEnabledRef = useRef(markerEnabled);
  const coordinatesContainerRef = useRef(null);
  const instructionsRef = useRef(null);
  const longRef = useRef(null);
  const latRef = useRef(null);
  const eleRef = useRef(null);
  const mapContainerRef = useRef(null); // create a reference to the map container
  const theme = useSelector((state) => state.themeReducer);

  useEffect(() => {
    markerEnabledRef.current = markerEnabled;
  }, [markerEnabled]);

  const popUpRef = useRef(
    new mapboxgl.Popup({
      maxWidth: "400px",
      offset: 15,
      focusAfterOpen: false,
    })
  );

  const { data } = useQuery(
    ["ui-map-wells"],
    async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/ui-map-wells`
        );

        //filters out any well that does not have geometry data
        const filterData = data.filter(
          (location) => location.location_geometry
        );
        return filterData;
      } catch (err) {
        console.error(err);
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  async function getElevation() {
    // Construct the API request.
    const query = await fetch(
      `https://api.mapbox.com/v4/mapbox.mapbox-terrain-v2/tilequery/${longRef.current.innerHTML},${latRef.current.innerHTML}.json?layers=contour&limit=50&access_token=${mapboxgl.accessToken}`,
      { method: "GET" }
    );
    if (query.status !== 200) return;
    const data = await query.json();

    const allFeatures = data.features;

    const elevations = allFeatures.map((feature) => feature.properties.ele);
    const elevation = (Math.max(...elevations) * 3.28084).toFixed(4);

    eleRef.current.innerHTML = elevation;
  }

  //create map and apply all controls
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: BASEMAP_STYLES[2].url,
      center:
        !filterValues.reg_lon_dd || !filterValues.reg_lat_dd
          ? DEFAULT_MAP_CENTER
          : [filterValues.reg_lon_dd, filterValues.reg_lat_dd],
      zoom: !filterValues.reg_lon_dd || !filterValues.reg_lat_dd ? 8 : 16,
    });

    //top left controls
    map.addControl(
      new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        localGeocoder: coordinatesGeocoder,
        zoom: 16,
        mapboxgl: mapboxgl,
        reverseGeocode: true,
        placeholder: "Address/Coords Search",
        limit: 3,
      }),
      "top-left"
    );
    map.addControl(new mapboxgl.NavigationControl(), "top-left");
    map.addControl(new mapboxgl.FullscreenControl(), "top-left");

    //bottom left controls
    map.addControl(
      new mapboxgl.ScaleControl({ unit: "imperial" }),
      "bottom-left"
    );
    map.addControl(
      new RulerControl({
        units: "feet",
        labelFormat: (n) => `${n.toFixed(2)} ft`,
      }),
      "bottom-left"
    );

    map.on("load", () => {
      setMapIsLoaded(true);
      setMap(map);
    });
  }, []); // eslint-disable-line

  //resizes map when mapContainerRef dimensions changes (sidebar toggle)
  useEffect(() => {
    if (map) {
      const resizer = new ResizeObserver(debounce(() => map.resize(), 100));
      resizer.observe(mapContainerRef.current);
      return () => {
        resizer.disconnect();
      };
    }
  }, [map]);

  const [Marker, setMarker] = useState();

  useEffect(() => {
    if (mapIsLoaded && data?.length > 0 && typeof map != "undefined") {
      const marker = new mapboxgl.Marker({
        color: "red",
      })
        .setLngLat(
          !filterValues.reg_lon_dd || !filterValues.reg_lat_dd
            ? DEFAULT_MAP_CENTER
            : [filterValues.reg_lon_dd, filterValues.reg_lat_dd]
        )
        .addTo(map);

      setMarker(marker);

      if (filterValues.reg_lon_dd && filterValues.reg_lat_dd) {
        const lngLat = marker.getLngLat();
        coordinatesContainerRef.current.style.display = "block";
        instructionsRef.current.innerHTML =
          "Click on the map to generate coordinates and elevation fields";
        longRef.current.innerHTML = lngLat.lng.toFixed(5);
        latRef.current.innerHTML = lngLat.lat.toFixed(5);

        if (filterValues?.reg_well_elev_ft === null) {
          getElevation();
        } else {
          eleRef.current.innerHTML = (+filterValues?.reg_well_elev_ft)?.toFixed(
            4
          );
        }
      }
      if (!map.getSource("management-area")) {
        map.addSource("management-area", {
          type: "vector",
          url: "mapbox://bseacd.7ycci5rr",
        });

        map.addLayer(managementAreaFill);
        map.addLayer(managementAreaLine);
      }

      if (!map.getSource("counties")) {
        map.addSource("counties", {
          type: "vector",
          url: "mapbox://bseacd.5qlfm0ga",
        });

        map.addLayer(countiesFill);
        map.addLayer(countiesLine);
      }

      if (!map.getSource("locations")) {
        map.addSource("locations", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: data.map((location) => {
              return {
                type: "Feature",
                id: location.well_ndx,
                properties: {
                  ...location,
                },
                geometry: {
                  type: location.location_geometry.type,
                  coordinates: location.location_geometry.coordinates,
                },
              };
            }),
          },
        });

        if (!map.getLayer("locations")) {
          map.addLayer({
            ...locationsLayer,
            layout: {
              visibility: "none",
            },
          });
          //add labels for locations points
          map.addLayer({
            ...locationsLabelsLayer,
            layout: {
              visibility: "none",
            },
          });
        }
      }

      // //handles copying coordinates and measurements to the clipboard
      const copyableRefs = [longRef, latRef, eleRef];
      copyableRefs.forEach((ref) => {
        ref.current.addEventListener("click", (e) =>
          handleCopyCoords(e.target.textContent)
        );
      });

      map.on("click", (e) => {
        if (!markerEnabledRef.current) return;
        const { lng, lat } = e.lngLat;
        marker.setLngLat([lng, lat]);

        coordinatesContainerRef.current.style.display = "block";
        instructionsRef.current.innerHTML =
          "Click coordinate or elevation to copy individual result to clipboard";
        latRef.current.innerHTML = lat.toFixed(5);
        longRef.current.innerHTML = lng.toFixed(5);

        setFilterValues("reg_lat_dd", lat.toFixed(5));
        setFilterValues("reg_lon_dd", lng.toFixed(5));

        getElevation().then(() =>
          setFilterValues("reg_well_elev_ft", +eleRef.current.innerHTML)
        );
      });

      map.on("click", (e) => {
        map.fire("closeAllPopups");

        const features = map.queryRenderedFeatures(e.point);

        const coordinates = [e.lngLat.lng, e.lngLat.lat];

        const popupLayerIds = [
          "locations",
          "management-area-fill",
          "counties-fill",
        ];

        const myFeatures = features.filter((feature) =>
          popupLayerIds.includes(feature?.layer?.id)
        );

        if (myFeatures.length > 0) {
          const popupNode = document.createElement("div");
          ReactDOM.render(
            //MJB adding style providers to the popup
            <StylesProvider jss={jss}>
              <MuiThemeProvider theme={createTheme(theme.currentTheme)}>
                <ThemeProvider theme={createTheme(theme.currentTheme)}>
                  <Popup
                    currentUser={currentUser}
                    renderGraphButtons={false}
                    layers={[locationsLayer, managementAreaFill]}
                    features={myFeatures}
                    height="144px"
                    width="252px"
                    size="small"
                  />
                </ThemeProvider>
              </MuiThemeProvider>
            </StylesProvider>,
            popupNode
          );
          popUpRef.current
            .setLngLat(coordinates)
            .setDOMContent(popupNode)
            .addTo(map);
        }
      });

      // Change the cursor to a pointer when the mouse is over the places layer.
      map.on("mouseenter", "locations", () => {
        map.getCanvas().style.cursor = "pointer";
      });

      // Change it back to a pointer when it leaves.
      map.on("mouseleave", "locations", () => {
        map.getCanvas().style.cursor = "";
      });
    }
  }, [mapIsLoaded, map, data]); //eslint-disable-line

  const locationsLayers = ["locations-labels", "locations"];
  useEffect(() => {
    if (
      map !== undefined &&
      map.getLayer("locations") &&
      map.getLayer("locations-labels")
    ) {
      if (!locationsVisible) {
        locationsLayers.forEach((layer) =>
          map.setLayoutProperty(layer, "visibility", "none")
        );
      } else {
        locationsLayers.forEach((layer) =>
          map.setLayoutProperty(layer, "visibility", "visible")
        );
      }
    }
  }, [locationsVisible]); // eslint-disable-line

  const bellParcelLayers = ["management-area-fill", "management-area-line"];
  useEffect(() => {
    if (
      map !== undefined &&
      map.getLayer("management-area-fill") &&
      map.getLayer("management-area-line")
    ) {
      if (!zonesVisible) {
        bellParcelLayers.forEach((layer) =>
          map.setLayoutProperty(layer, "visibility", "none")
        );
      } else {
        bellParcelLayers.forEach((layer) =>
          map.setLayoutProperty(layer, "visibility", "visible")
        );
      }
    }
  }, [zonesVisible]); // eslint-disable-line

  const countiesLayers = ["counties-fill", "counties-line"];
  useEffect(() => {
    if (
      map !== undefined &&
      map.getLayer("counties-fill") &&
      map.getLayer("counties-line")
    ) {
      if (!countiesVisible) {
        countiesLayers.forEach((layer) =>
          map.setLayoutProperty(layer, "visibility", "none")
        );
      } else {
        countiesLayers.forEach((layer) =>
          map.setLayoutProperty(layer, "visibility", "visible")
        );
      }
    }
  }, [countiesVisible]); // eslint-disable-line

  const [isCoordsLoaded, setIsCoordsLoaded] = useState(false);
  useEffect(() => {
    if (map && Marker) {
      if (
        filterValues.reg_lat_dd > 90 ||
        filterValues.reg_lat_dd < -90 ||
        filterValues.reg_lon_dd > 180 ||
        filterValues.reg_lon_dd < -180 ||
        filterValues.reg_lon_dd === "-" ||
        filterValues.reg_lat_dd === "-"
      )
        return;

      if (
        filterValues.reg_lat_dd === null ||
        filterValues.reg_lon_dd === null
      ) {
        coordinatesContainerRef.current.style.display = "block";
        latRef.current.innerHTML = "N/A";
        longRef.current.innerHTML = "N/A";
        eleRef.current.innerHTML = "N/A";

        Marker.setLngLat(DEFAULT_MAP_CENTER);

        map.flyTo({
          center: DEFAULT_MAP_CENTER,
          zoom: 8,
          padding: { bottom: 0 },
        });
      } else {
        if (isCoordsLoaded) {
          setFilterValues(
            "reg_lat_dd",
            (+filterValues?.reg_lat_dd)?.toFixed(5)
          );
          setFilterValues(
            "reg_lon_dd",
            (+filterValues?.reg_lon_dd)?.toFixed(5)
          );
        }

        coordinatesContainerRef.current.style.display = "block";
        latRef.current.innerHTML = (+filterValues?.reg_lat_dd)?.toFixed(5);
        longRef.current.innerHTML = (+filterValues?.reg_lon_dd)?.toFixed(5);

        if (filterValues?.reg_well_elev_ft === null) {
          getElevation();
        } else {
          eleRef.current.innerHTML = (+filterValues?.reg_well_elev_ft)?.toFixed(
            4
          );
        }

        Marker.setLngLat(
          !filterValues.reg_lon_dd || !filterValues.reg_lat_dd
            ? DEFAULT_MAP_CENTER
            : [filterValues.reg_lon_dd, filterValues.reg_lat_dd]
        );

        map.flyTo({
          center:
            !filterValues.reg_lon_dd || !filterValues.reg_lat_dd
              ? DEFAULT_MAP_CENTER
              : [filterValues.reg_lon_dd, filterValues.reg_lat_dd],
          zoom: !filterValues.reg_lon_dd || !filterValues.reg_lat_dd ? 8 : 16,
          padding: { bottom: 0 },
        });
        setIsCoordsLoaded(true);
      }
    } // eslint-disable-next-line
  }, [
    map,
    filterValues.lon_dd,
    filterValues.lat_dd,
    filterValues.reg_well_elev_ft,
  ]);

  useEffect(() => {
    if (filterValues.well_ndx) setIsCoordsLoaded(false);
  }, [filterValues.well_ndx]);

  return (
    <>
      <Container>
        <MapContainer ref={mapContainerRef}>
          <LocationsControl
            open={locationsVisible}
            onToggle={() => setLocationsVisible(!locationsVisible)}
            top={10}
          />

          <ZonesControl
            open={zonesVisible}
            onToggle={() => setZonesVisible(!zonesVisible)}
            top={50}
            title="Management Area"
          />

          <MarkerControl
            open={markerEnabled}
            onToggle={() => setMarkerEnabled(!markerEnabled)}
            top={130}
            title={
              markerEnabled
                ? "Disable Marker Placement"
                : "Enable Marker Placement"
            }
          />

          <CountiesControl
            open={countiesVisible}
            onToggle={() => setCountiesVisible(!countiesVisible)}
            top={90}
          />

          <CoordinatesPopup
            coordinatesContainerRef={coordinatesContainerRef}
            longRef={longRef}
            latRef={latRef}
            eleRef={eleRef}
            title="Red marker:"
          />

          <Instructions ref={instructionsRef}>
            Click on the map to generate coordinates and elevation fields
          </Instructions>
        </MapContainer>
      </Container>
    </>
  );
};

export default RegLatLongMap;
